import {
  ArtButton,
  ArtDialog,
  ArtSelect,
  ArtTable,
  ArtTableHandle,
  ArtTextField,
  confirmDialog,
  HeadCell,
  SSRTableBuilder,
  useApiService,
  useIdpService,
} from "@art/shared-ui";
import { Chip, Stack, Tooltip } from "@mui/material";
import {
  faBan,
  faCopy,
  faKey,
  faPlus,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePageWrapper } from "../../../hooks";
import { useAttributePermission } from "../../permissions";
import styles from "./api-key-management.module.scss";
import { tableParamNav } from "../../../utils/table-utils";

interface ServicePrincipal {
  id: number;
  client_id: string;
  title: string;
  is_expired?: boolean;
}

interface ResourceScope {
  id?: number;
  project_key?: string;
  environment_name?: string;
  resource_type?: string;
  resource_id?: string;
}

interface ManagedAPIKey {
  id: number;
  key: {
    id: number;
    label: string;
    key_prefix: string;
    created_at: string;
    expires_at?: string;
    last_used_at?: string;
    rotated_at?: string;
    revoked_at?: string;
    is_revoked: boolean;
  };
  service_principal: ServicePrincipal;
  roles: string[];
  resource_scopes: ResourceScope[];
}

interface SecretResult {
  value: string;
  action: "created" | "rotated";
}

const formatDate = (value?: string) => {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const keyStatus = (row: ManagedAPIKey) => {
  if (row.key.is_revoked)
    return { label: "Revoked", color: "default" as const };
  if (
    row.key.expires_at &&
    new Date(row.key.expires_at).getTime() <= Date.now()
  ) {
    return { label: "Expired", color: "warning" as const };
  }
  return { label: "Active", color: "success" as const };
};

const scopeLabel = (scope: ResourceScope) =>
  [
    scope.project_key,
    scope.environment_name,
    scope.resource_type,
    scope.resource_id,
  ]
    .filter(Boolean)
    .join(" / ");

function APIKeyManagement() {
  const tableRef = useRef<ArtTableHandle>(null);
  const apiService = useApiService();
  const idpService = useIdpService();
  const tenantName = apiService.getStore().getState().auth.tenantName as string;
  const endpoint = `api/tenants/${encodeURIComponent(tenantName)}/api-keys`;

  const canCreate = useAttributePermission(
    "PLATFORM_SETTINGS",
    "API_KEY_MANAGEMENT",
    "CreateAPIKeys",
  );
  const canRotate = useAttributePermission(
    "PLATFORM_SETTINGS",
    "API_KEY_MANAGEMENT",
    "RotateAPIKeys",
  );
  const canRevoke = useAttributePermission(
    "PLATFORM_SETTINGS",
    "API_KEY_MANAGEMENT",
    "RevokeAPIKeys",
  );

  const [queryParams, setQueryParams]: any = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [clientID, setClientID] = useState<number | "">("");
  const [expiresAt, setExpiresAt] = useState("");
  const [principals, setPrincipals] = useState<ServicePrincipal[]>([]);
  const [principalsLoading, setPrincipalsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [secret, setSecret] = useState<SecretResult | null>(null);

  const loadPrincipals = useCallback(async () => {
    setPrincipalsLoading(true);
    try {
      const response = await apiService.get("/client/configuration", {
        params: { page: 1, limit: 1000 },
      });
      const records = response?.data?.data?.data ?? [];
      setPrincipals(
        records.filter((record: ServicePrincipal) => !record.is_expired),
      );
    } finally {
      setPrincipalsLoading(false);
    }
  }, [apiService]);

  const openCreate = async () => {
    setLabel("");
    setClientID("");
    setExpiresAt("");
    setCreateOpen(true);
    await loadPrincipals();
  };

  const closeCreate = () => {
    if (!submitting) setCreateOpen(false);
  };

  const createKey = async () => {
    if (!label.trim() || !clientID) return;
    setSubmitting(true);
    try {
      const response = await idpService.post(endpoint, {
        label: label.trim(),
        client_id: clientID,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      const rawKey = response?.data?.data?.api_key;
      setCreateOpen(false);
      setSecret({ value: rawKey, action: "created" });
      tableRef.current?.refresh(0);
      idpService.showSnack("API key created", "success");
    } finally {
      setSubmitting(false);
    }
  };

  const rotateKey = async (row: ManagedAPIKey) => {
    if (row.key.is_revoked) {
      idpService.showSnack("Revoked keys cannot be rotated", "warning");
      return;
    }
    if (
      row.key.expires_at &&
      new Date(row.key.expires_at).getTime() <= Date.now()
    ) {
      idpService.showSnack(
        "Expired keys cannot be rotated; create a new key instead",
        "warning",
      );
      return;
    }
    try {
      await confirmDialog(
        "Rotate API key",
        `Rotate ${row.key.label}? The current key will stop working immediately.`,
      );
    } catch {
      return;
    }
    const response = await idpService.post(
      `${endpoint}/${row.key.id}/rotate`,
      {},
    );
    setSecret({ value: response?.data?.data?.api_key, action: "rotated" });
    tableRef.current?.refresh();
    idpService.showSnack("API key rotated", "success");
  };

  const revokeKey = async (row: ManagedAPIKey) => {
    if (row.key.is_revoked) {
      idpService.showSnack("This API key is already revoked", "warning");
      return;
    }
    try {
      await confirmDialog(
        "Revoke API key",
        `Revoke ${row.key.label}? Any integration using this key will lose access immediately.`,
      );
    } catch {
      return;
    }
    await idpService.post(`${endpoint}/${row.key.id}/revoke`, {});
    tableRef.current?.refresh();
    idpService.showSnack("API key revoked", "success");
  };

  const copySecret = async () => {
    if (!secret?.value) return;
    await navigator.clipboard.writeText(secret.value);
    idpService.showSnack("API key copied", "success");
  };

  const builder = useMemo(
    () =>
      SSRTableBuilder<ManagedAPIKey>({
        endpoint,
        method: "get",
        apiService: idpService,
        onFetch: (params) => {
          // return {
          //     "page": params["page"],
          //     "search_text": params["search_text"]
          // }
          let currentPage = params["page"] || "1";
          let searchText = params["search_text"] || "";

          const newParams: any = {
            page: currentPage,
          };
          if (searchText) {
            newParams.search = searchText;
          }
          setQueryParams(
            newParams,
            tableParamNav(queryParams, "page", currentPage),
          );
          return {
            page: currentPage,
            search_text: searchText,
          };
        },

        onResponse: (response: any) => {
          // Supports both the Axios response and the unwrapped API payload.
          const payload = response?.data ?? response;

          const records = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];

          const rows: ManagedAPIKey[] = records.map((row: any) => ({
            ...row,
            id: row.key?.id ?? row.id,
            roles: Array.isArray(row.roles) ? row.roles : [],
            resource_scopes: Array.isArray(row.resource_scopes)
              ? row.resource_scopes
              : [],
          }));

          return {
            data: rows,
            total: Math.max(Number(payload?.total_count) || 0, rows.length),
          };
        },
      }),
    [endpoint, idpService],
  );

  const headCells = useMemo<HeadCell<ManagedAPIKey>[]>(
    () => [
      {
        id: "key",
        numeric: false,
        disablePadding: false,
        label: "Key",
        getColData: (row) => (
          <div className={styles.identityCell}>
            <strong>{row.key.label}</strong>
            <span className={styles.prefix}>{row.key.key_prefix}••••••••</span>
          </div>
        ),
      },
      {
        id: "service_principal",
        numeric: false,
        disablePadding: false,
        label: "Service principal",
        getColData: (row) => (
          <div className={styles.principalCell}>
            <strong>{row.service_principal?.title || "Unavailable"}</strong>
            <span>{row.service_principal?.client_id || "—"}</span>
          </div>
        ),
      },
      {
        id: "roles",
        numeric: false,
        disablePadding: false,
        label: "Roles",
        getColData: (row) => (
          <div className={styles.scopeSummary}>
            {row.roles?.length ? (
              row.roles
                .slice(0, 2)
                .map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    variant="outlined"
                  />
                ))
            ) : (
              <span className={styles.muted}>No roles</span>
            )}
            {(row.roles?.length ?? 0) > 2 && (
              <Chip label={`+${row.roles.length - 2}`} size="small" />
            )}
          </div>
        ),
      },
      {
        id: "resource_scopes",
        numeric: false,
        disablePadding: false,
        label: "Resource access",
        getColData: (row) => {
          const labels = (row.resource_scopes ?? [])
            .map(scopeLabel)
            .filter(Boolean);
          return labels.length ? (
            <Tooltip title={labels.join("\n")}>
              <Chip
                label={`${labels.length} scope${labels.length === 1 ? "" : "s"}`}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          ) : (
            <span className={styles.muted}>Role defaults</span>
          );
        },
      },
      {
        id: "key",
        numeric: false,
        disablePadding: false,
        label: "Last used",
        getColData: (row) => (
          <span className={styles.muted}>
            {formatDate(row.key.last_used_at)}
          </span>
        ),
      },
      {
        id: "key",
        numeric: false,
        disablePadding: false,
        label: "Expires",
        getColData: (row) => (
          <span className={styles.muted}>
            {row.key.expires_at ? formatDate(row.key.expires_at) : "No expiry"}
          </span>
        ),
      },
      {
        id: "key",
        numeric: false,
        disablePadding: false,
        label: "Status",
        getColData: (row) => {
          const status = keyStatus(row);
          return (
            <Chip label={status.label} color={status.color} size="small" />
          );
        },
      },
    ],
    [],
  );

  const rowActions = [
    canRotate && { icon: faRotate, label: "Rotate", onClick: rotateKey },
    canRevoke && { icon: faBan, label: "Revoke", onClick: revokeKey },
  ].filter(Boolean) as any;

  return (
    <>
      <div className={styles.intro}>
        <div className={styles.introCopy}>
          <h2>API keys</h2>
          <p>
            Authenticate services without a user session. Each key inherits the
            roles and resource access of its service principal.
          </p>
        </div>
      </div>

      <ArtTable
        ref={tableRef}
        headCells={headCells}
        builder={builder}
        disablePagination
        disableEmptyRows
        dataGuide="api-keys"
        toolbarActions={
          canCreate
            ? [
                <ArtButton
                  key="create-api-key"
                  label="Create API key"
                  icon={faPlus}
                  fullWidth={false}
                  onClick={openCreate}
                />,
              ]
            : undefined
        }
        rowActions={rowActions.length ? rowActions : undefined}
      />

      <ArtDialog
        open={createOpen}
        onClose={closeCreate}
        title="Create API key"
        size="small"
        actions={
          <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1}>
            <ArtButton
              label="Cancel"
              variant="outlined"
              color="inherit"
              fullWidth={false}
              disabled={submitting}
              onClick={closeCreate}
            />
            <ArtButton
              label={submitting ? "Creating…" : "Create key"}
              icon={faKey}
              fullWidth={false}
              disabled={submitting || !label.trim() || !clientID}
              onClick={createKey}
            />
          </Stack>
        }
      >
        <p className={styles.dialogCopy}>
          Choose a service principal whose roles and project/environment scopes
          this key should inherit.
        </p>
        <ArtTextField
          label="Key name"
          value={label}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setLabel(event.target.value)
          }
          placeholder="For example, production ingestion"
          required
          marginBottom={18}
        />
        <ArtSelect
          label="Service principal"
          value={clientID}
          onChange={(event: any) => setClientID(Number(event.target.value))}
          options={principals.map((principal) => ({
            value: principal.id,
            label: `${principal.title} · ${principal.client_id}`,
          }))}
          placeholder={
            principalsLoading
              ? "Loading service principals…"
              : "Select a service principal"
          }
          disabled={principalsLoading}
          required
          marginBottom={18}
        />
        <ArtTextField
          label="Expiry (optional)"
          type="datetime-local"
          value={expiresAt}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setExpiresAt(event.target.value)
          }
          inputProps={{ min: new Date().toISOString().slice(0, 16) }}
          marginBottom={0}
        />
      </ArtDialog>

      <ArtDialog
        open={Boolean(secret)}
        onClose={() => setSecret(null)}
        title={
          secret?.action === "rotated" ? "API key rotated" : "API key created"
        }
        size="small"
        actions={
          <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1}>
            <ArtButton
              label="Done"
              variant="outlined"
              color="inherit"
              fullWidth={false}
              onClick={() => setSecret(null)}
            />
            <ArtButton
              label="Copy key"
              icon={faCopy}
              fullWidth={false}
              onClick={copySecret}
            />
          </Stack>
        }
      >
        <div className={styles.secretNotice}>
          Copy this key now. For security, ART stores only its hash and cannot
          show it again.
        </div>
        <div className={styles.secretValue} data-qa="api-key-secret">
          {secret?.value}
        </div>
      </ArtDialog>
    </>
  );
}

export default usePageWrapper(APIKeyManagement, { page_title: "API Keys" });
